import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Skeleton,
  Chip,
  Drawer,
  Divider,
  IconButton,
  Stack,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../api/axios";

const ProductQnA = ({ productId, productVendorId }) => {
  const [questions, setQuestions] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [question, setQuestion] = useState("");
  const [posting, setPosting] = useState(false);

  const [search, setSearch] = useState("");
  const [suggestions, setSuggestions] = useState([]);

  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);

  const [answerText, setAnswerText] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const isProductOwner = user?.role === "vendor" && user?._id === productVendorId;

  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    try {
      const qRes = await api.get(`/products/${productId}/questions`);
      const questionsData = qRes.data || [];
      
      setQuestions(questionsData);
      // FAQ section shows questions that have answers (topAnswer)
      const faqQuestions = questionsData.filter(q => q.topAnswer).slice(0, 5);
      setFaqs(faqQuestions);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (productId) loadData();
  }, [productId]);

  /* ================= ASK ================= */
  const handleAsk = async () => {
    if (!question.trim()) return;
    if (!user) return alert("Login required");

    await api.post(`/products/${productId}/questions`, {
      questionText: question,
    });

    setQuestion("");
    loadData();
  };

  /* ================= ANSWERS ================= */
  const openAnswers = async (q) => {
    setSelectedQuestion(q);
    setAnswersLoading(true);

    const res = await api.get(`/products/questions/${q._id}/answers`);
    setAnswers(res.data || []);
    setAnswersLoading(false);
  };

  const handleAnswer = async () => {
    if (!answerText.trim()) return;
    if (!user) return alert("Login required");

    await api.post(
      `/products/questions/${selectedQuestion._id}/answers`,
      { answerText }
    );

    setAnswerText("");
    openAnswers(selectedQuestion);
  };

   /* ================= VOTING ================= */
  const handleVote = async (entityId, entityType, voteType) => {
    try {
      await api.post(`/products/vote`, {
        entityId,
        entityType,
        voteType,
      });

      setQuestions((prev) =>
        prev.map((q) =>
          q._id === entityId
            ? {
                ...q,
                upvotes:
                  voteType === "upvote"
                    ? q.upvotes + 1
                    : q.upvotes,
                downvotes:
                  voteType === "downvote"
                    ? q.downvotes + 1
                    : q.downvotes,
              }
            : q
        )
      );
    } catch (err) {
      console.error("Vote failed", err);
    }
  };

  /* ================= FILTER ================= */
  const filtered = questions.filter((q) =>
    q.questionText.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      <Paper sx={{ mt: 3, p: 3, borderRadius: 3 }}>
        {/* ================= FAQ SECTION ================= */}
        <Typography fontWeight={700} mb={2}>
          Frequently Asked Questions
        </Typography>

        {loading ? (
          <Skeleton height={40} />
        ) : faqs.length === 0 ? null : (
          faqs.map((q) => (
            <Box
              key={q._id}
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                "&:hover": { bgcolor: "#f7f7f7" },
              }}
            >
              <Typography fontWeight={600}>
                Q: {q.questionText}
              </Typography>

              {q.topAnswer && (
                <Typography
                  sx={{ mt: 0.5, color: "#555" }}
                >
                  A: {q.topAnswer.answerText}
                </Typography>
              )}
            </Box>
          ))
        )}

        <Divider sx={{ my: 3 }} />

        {/* ================= QNA ================= */}
        <Typography fontWeight={700} mb={2}>
          Questions & Answers
        </Typography>

        {/* SEARCH */}
        <TextField
          fullWidth
          placeholder="Search questions..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* ASK */}
        <TextField
          fullWidth
          placeholder="Have a question? Ask here..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />

        <Button
          variant="contained"
          sx={{ mt: 1 }}
          disabled={!question.trim()}
          onClick={handleAsk}
        >
          Ask Question
        </Button>

        {/* LIST */}
        {loading ? (
          <Skeleton height={40} />
        ) : filtered.map((q) => (
            <Box
              key={q._id}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                border: "1px solid #eee",
                "&:hover": {
                  boxShadow: 1,
                },
              }}
            >
              <Typography fontWeight={600}>
                Q: {q.questionText}
              </Typography>

              {q.topAnswer && (
                <Box sx={{ mt: 1 }}>
                  <Typography>
                    A: {q.topAnswer.answerText}
                  </Typography>

                  {q.topAnswer.isVendorAnswer && (
                    <Chip
                      label="Seller"
                      size="small"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
              )}

              <Stack direction="row" spacing={1} mt={1}>
                <IconButton size="small"
                onClick={() =>
                    handleVote(q._id, "question", "upvote")
                  }
                >
                  <ThumbUpIcon fontSize="small" />
                  {q.upvotes}
                </IconButton>

                <IconButton size="small"
                onClick={() =>
                    handleVote(q._id, "question", "downvote")
                  }
                >
                  <ThumbDownIcon fontSize="small" />
                  {q.downvotes}
                </IconButton>

                <Button
                  size="small"
                  onClick={() => openAnswers(q)}
                >
                  View answers ({q.answerCount || 0})
                </Button>
              </Stack>
            </Box>
          ))}
      </Paper>

      {/* DRAWER */}
      <Drawer
        anchor="right"
        open={!!selectedQuestion}
        onClose={() => setSelectedQuestion(null)}
      >
        <Box sx={{ width: 400, p: 2 }}>
          <Typography fontWeight={700}>
            Answers
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography fontWeight={600}>
            Q: {selectedQuestion?.questionText}
          </Typography>

          {/* ANSWER FORM — ONLY VISIBLE TO PRODUCT OWNER VENDOR */}
          {isProductOwner && (
            <>
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                placeholder="Write answer..."
                value={answerText}
                onChange={(e) => setAnswerText(e.target.value)}
              />

              <Button
                variant="contained"
                sx={{ mt: 1 }}
                onClick={handleAnswer}
              >
                Post Answer
              </Button>
            </>
          )}

          {/* ANSWER LIST — VISIBLE TO EVERYONE */}
          {answersLoading ? (
            <Skeleton height={40} />
          ) : answers.length === 0 ? (
            <Typography sx={{ mt: 2, color: "#999" }}>
              No answers yet.
            </Typography>
          ) : (
            answers.map((a) => (
              <Box key={a._id} sx={{ mt: 2 }}>
                <Typography>{a.answerText}</Typography>
                {a.isVendorAnswer && (
                  <Chip label="Seller" size="small" />
                )}
              </Box>
            ))
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default ProductQnA;